import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Stack,
  Alert,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { authenticatedFetch } from "../../utils/api";
import { truncateText, PROGRAM_NAME_MAX_WIDTH } from "../../utils/formatters";
import { useTranslation } from "../../i18n";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

interface Company {
  id: number;
  name: string;
}

interface ProgramDetail {
  id: number;
  dia: number;
  gauge: string;
  program_type: string;
  weight_in_kilo: number;
  status?: string;
  completed_weight?: number;
}

interface Program {
  id: number;
  company: {
    id: number;
    name: string;
  };
  lot?: string;
  program_details?: ProgramDetail[];
}

const deliveryDetailSchema = z.object({
  dia: z.string().min(1, "Dia is required"),
  gauge: z.string().min(1, "Gauge is required"),
  machine_type: z.string().min(1, "Machine type is required"),
  rolls: z
    .union([z.number(), z.string()])
    .pipe(z.coerce.number({ required_error: "Rolls is required" }))
    .refine((val) => Number.isInteger(val), "Rolls must be an integer")
    .refine((val) => val > 0, "Rolls must be positive"),
  weight: z
    .union([z.number(), z.string()])
    .pipe(z.coerce.number({ required_error: "Weight is required" }))
    .refine((val) => val > 0, "Weight must be positive"),
});

const deliverySchema = z.object({
  company_id: z.number({ required_error: "Company is required" }),
  program_id: z.number({ required_error: "Program is required" }),
  delivery_person_name: z
    .string()
    .min(1, "Delivery person name is required"),
  delivery_person_phone: z
    .string()
    .min(1, "Delivery person phone is required"),
  delivery_vehicle_number: z
    .string()
    .min(1, "Vehicle number is required"),
  total_rolls: z
    .union([z.number(), z.string()])
    .pipe(z.coerce.number())
    .optional(),
  total_weight: z
    .union([z.number(), z.string()])
    .pipe(z.coerce.number())
    .optional(),
  delivery_details_attributes: z
    .array(deliveryDetailSchema)
    .min(1, "At least one delivery detail is required"),
});

type DeliveryFormData = z.infer<typeof deliverySchema>;

export default function CreateDelivery() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const [error, setError] = useState<string>("");
  const [companies, setCompanies] = useState<Company[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [filteredPrograms, setFilteredPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);

  const { control, handleSubmit, watch } = useForm<DeliveryFormData>({
    resolver: zodResolver(deliverySchema),
    defaultValues: {
      company_id: undefined,
      program_id: undefined,
      delivery_person_name: "",
      delivery_person_phone: "",
      delivery_vehicle_number: "",
      total_rolls: undefined,
      total_weight: undefined,
      delivery_details_attributes: [
        {
          dia: "",
          gauge: "",
          machine_type: "",
          rolls: undefined as any,
          weight: undefined as any,
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "delivery_details_attributes",
  });

  const selectedCompanyId = watch("company_id");
  const selectedProgramId = watch("program_id");
  const deliveryDetails = watch("delivery_details_attributes");

  useEffect(() => {
    fetchData();
  }, []);

  // Validation logic to ensure dia/gauge/machine_type combinations are valid
  useEffect(() => {
    fields.forEach((field, index) => {
      const dia = watch(`delivery_details_attributes.${index}.dia`);
      const gauge = watch(`delivery_details_attributes.${index}.gauge`);
      const machineType = watch(`delivery_details_attributes.${index}.machine_type`);

      // Validate dia/gauge/machine_type combination when any changes
      if (selectedProgramId && dia && gauge && machineType) {
        const program = programs.find((p) => p.id === selectedProgramId);
        if (program && program.program_details) {
          // Check if the dia/gauge/machine_type combination exists in program details
          const validCombination = program.program_details.some(
            (d) => 
              d.dia.toString() === dia && 
              d.gauge === gauge && 
              d.program_type === machineType
          );

          // If combination is invalid, determine which field to clear
          if (!validCombination) {
            // Get available options based on current selections
            const availableGauges = getAvailableGauges(selectedProgramId, dia, machineType);
            const availableDias = getAvailableDias(selectedProgramId, gauge, machineType);
            const availableMachineTypes = getAvailableMachineTypes(selectedProgramId, dia, gauge);

            // Clear incompatible selections
            if (gauge && !availableGauges.includes(gauge)) {
              const currentValue = deliveryDetails[index];
              if (currentValue) {
                // Update using setValue instead of modifying the array directly
                const newDetails = [...deliveryDetails];
                newDetails[index] = { ...currentValue, gauge: "" };
              }
            }

            if (dia && !availableDias.map(d => d.toString()).includes(dia)) {
              const currentValue = deliveryDetails[index];
              if (currentValue) {
                const newDetails = [...deliveryDetails];
                newDetails[index] = { ...currentValue, dia: "" };
              }
            }

            if (machineType && !availableMachineTypes.includes(machineType)) {
              const currentValue = deliveryDetails[index];
              if (currentValue) {
                const newDetails = [...deliveryDetails];
                newDetails[index] = { ...currentValue, machine_type: "" };
              }
            }
          }
        }
      }
    });
  }, [watch, fields, programs, selectedProgramId]);

  useEffect(() => {
    if (selectedCompanyId) {
      const filtered = programs.filter(
        (program) => program.company.id === selectedCompanyId
      );
      setFilteredPrograms(filtered);
    } else {
      setFilteredPrograms([]);
    }
  }, [selectedCompanyId, programs]);

  const fetchData = async () => {
    try {
      const [companiesRes, programsRes] = await Promise.all([
        authenticatedFetch(`${API_URL}/companies?per_page=1000&minimal=true`),
        authenticatedFetch(`${API_URL}/programs`),
      ]);

      if (companiesRes.status === 401 || programsRes.status === 401) {
        navigate("/login");
        return;
      }

      if (!companiesRes.ok || !programsRes.ok) {
        throw new Error("Failed to fetch data");
      }

      const companiesData = await companiesRes.json();
      const programsData = await programsRes.json();

      setCompanies(companiesData);
      setPrograms(programsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const getAvailableDias = (
    programId: number | undefined,
    selectedGauge?: string,
    selectedMachineType?: string
  ) => {
    if (!programId) return [];
    const program = programs.find((p) => p.id === programId);
    if (!program || !program.program_details) return [];

    // Filter by gauge and/or machine type if provided
    let filteredDetails = program.program_details;
    if (selectedGauge) {
      filteredDetails = filteredDetails.filter((d) => d.gauge === selectedGauge);
    }
    if (selectedMachineType) {
      filteredDetails = filteredDetails.filter((d) => d.program_type === selectedMachineType);
    }

    // Get unique dia values from filtered program details
    const dias = Array.from(new Set(filteredDetails.map((d) => d.dia)));
    return dias.sort((a, b) => a - b);
  };

  const getAvailableGauges = (
    programId: number | undefined,
    selectedDia?: string,
    selectedMachineType?: string
  ) => {
    if (!programId) return [];
    const program = programs.find((p) => p.id === programId);
    if (!program || !program.program_details) return [];

    // Filter by dia and/or machine type if provided
    let filteredDetails = program.program_details;
    if (selectedDia) {
      filteredDetails = filteredDetails.filter((d) => d.dia.toString() === selectedDia);
    }
    if (selectedMachineType) {
      filteredDetails = filteredDetails.filter((d) => d.program_type === selectedMachineType);
    }

    // Get unique gauge values from filtered program details
    const gauges = Array.from(new Set(filteredDetails.map((d) => d.gauge)));
    return gauges.sort();
  };

  const getAvailableMachineTypes = (
    programId: number | undefined,
    selectedDia?: string,
    selectedGauge?: string
  ) => {
    if (!programId) return [];
    const program = programs.find((p) => p.id === programId);
    if (!program || !program.program_details) return [];

    // Filter by dia and/or gauge if provided
    let filteredDetails = program.program_details;
    if (selectedDia) {
      filteredDetails = filteredDetails.filter((d) => d.dia.toString() === selectedDia);
    }
    if (selectedGauge) {
      filteredDetails = filteredDetails.filter((d) => d.gauge === selectedGauge);
    }

    // Get unique machine type values from filtered program details
    const machineTypes = Array.from(new Set(filteredDetails.map((d) => d.program_type)));
    return machineTypes.sort();
  };

  const onSubmit = async (data: DeliveryFormData) => {
    try {
      const response = await authenticatedFetch(`${API_URL}/deliveries`, {
        method: "POST",
        body: JSON.stringify({ delivery: data }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(JSON.stringify(errorData));
      }

      navigate("/deliveries");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const calculateTotalWeight = () => {
    return deliveryDetails
      ?.reduce((sum, detail) => sum + (detail?.weight || 0), 0)
      .toFixed(2);
  };

  const calculateTotalRolls = () => {
    return deliveryDetails?.reduce((sum, detail) => sum + (detail?.rolls || 0), 0);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ mt: { xs: 2, sm: 4 }, mb: 4 }}>
        {/* Header */}
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          alignItems={{ xs: 'flex-start', sm: 'center' }} 
          justifyContent="space-between"
          spacing={2} 
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton 
              onClick={() => navigate("/deliveries")} 
              color="primary" 
              size={isMobile ? "small" : "medium"}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant={isMobile ? "h6" : "h5"} component="h2">
              {t('deliveries.createTitle')}
            </Typography>
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          {/* Basic Information Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('basicInformation')}</Typography>
              <Stack spacing={2}>
                <Controller
                  name="company_id"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormControl
                      fullWidth
                      error={!!fieldState.error}
                    >
                      <InputLabel>{t('deliveries.company')} *</InputLabel>
                      <Select 
                        {...field} 
                        label={`${t('deliveries.company')} *`} 
                        value={field.value || ""}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 300,
                            },
                          },
                          disableScrollLock: true,
                        }}
                      >
                        {companies.map((company) => (
                          <MenuItem key={company.id} value={company.id}>
                            {company.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {fieldState.error && (
                        <FormHelperText>{fieldState.error.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />

                <Controller
                  name="program_id"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormControl
                      fullWidth
                      error={!!fieldState.error}
                      disabled={!selectedCompanyId}
                    >
                      <InputLabel>{t('deliveries.program')} *</InputLabel>
                      <Select 
                        {...field} 
                        label={`${t('deliveries.program')} *`} 
                        value={field.value || ""}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 300,
                            },
                          },
                          disableScrollLock: true,
                        }}
                      >
                        {filteredPrograms.map((program) => {
                          const programText = program.lot || `Program #${program.id}`;
                          return (
                            <MenuItem key={program.id} value={program.id} title={programText}>
                              {truncateText(programText, PROGRAM_NAME_MAX_WIDTH.select)}
                            </MenuItem>
                          );
                        })}
                      </Select>
                      {fieldState.error && (
                        <FormHelperText>{fieldState.error.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Delivery Person Information Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('deliveries.deliveryPersonDetails')}</Typography>
              <Stack spacing={2}>
                <Controller
                  name="delivery_person_name"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label={`${t('deliveries.deliveryPersonName')} *`}
                      fullWidth
                      size={isMobile ? "small" : "medium"}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="delivery_person_phone"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label={`${t('deliveries.deliveryPersonPhone')} *`}
                          fullWidth
                          size={isMobile ? "small" : "medium"}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="delivery_vehicle_number"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label={`${t('deliveries.vehicleNumber')} *`}
                          fullWidth
                          size={isMobile ? "small" : "medium"}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>

          {/* Delivery Details Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                spacing={2}
                sx={{ mb: 2 }}
              >
                <Typography variant="h6">{t('deliveries.deliveryDetails')}</Typography>
                <Button
                  variant="outlined"
                  size={isMobile ? "small" : "medium"}
                  startIcon={<AddIcon />}
                  fullWidth={isMobile}
                  onClick={() =>
                    append({
                      dia: "",
                      gauge: "",
                      machine_type: "",
                      rolls: undefined as any,
                      weight: undefined as any,
                    })
                  }
                >
                  {t('addDetail')}
                </Button>
              </Stack>

              {isMobile ? (
                /* Mobile Card View */
                <Stack spacing={2}>
                  {fields.length === 0 ? (
                    <Paper variant="outlined" sx={{ p: 3 }}>
                      <Typography variant="body2" color="text.secondary" align="center">
                        {t('noDetailsAdded')}
                      </Typography>
                    </Paper>
                  ) : (
                    fields.map((field, index) => {
                      const selectedDia = watch(`delivery_details_attributes.${index}.dia`);
                      const selectedGauge = watch(`delivery_details_attributes.${index}.gauge`);
                      const selectedMachineType = watch(`delivery_details_attributes.${index}.machine_type`);
                      
                      const availableDias = getAvailableDias(selectedProgramId, selectedGauge, selectedMachineType);
                      const availableGauges = getAvailableGauges(selectedProgramId, selectedDia, selectedMachineType);
                      const availableMachineTypes = getAvailableMachineTypes(selectedProgramId, selectedDia, selectedGauge);

                      return (
                        <Paper key={field.id} variant="outlined" sx={{ p: 2 }}>
                          <Stack spacing={2}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Typography variant="subtitle2" fontWeight="bold">{t('deliveries.detailNumber')}{index + 1}</Typography>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => remove(index)}
                                disabled={fields.length === 1}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>

                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <Controller
                                  name={`delivery_details_attributes.${index}.dia`}
                                  control={control}
                                  render={({ field, fieldState }) => (
                                    <FormControl
                                      fullWidth
                                      size="small"
                                      error={!!fieldState.error}
                                      disabled={!selectedProgramId}
                                    >
                                      <Typography variant="caption" color="text.secondary" gutterBottom>
                                        {t('programs.dia')} *
                                      </Typography>
                                      <Select
                                        {...field}
                                        value={field.value || ""}
                                        displayEmpty
                                        MenuProps={{
                                          PaperProps: {
                                            style: {
                                              maxHeight: 300,
                                            },
                                          },
                                          disableScrollLock: true,
                                        }}
                                      >
                                        <MenuItem value="" disabled>
                                          {!selectedProgramId ? "Select Program" : "Select"}
                                        </MenuItem>
                                        {availableDias.map((dia) => (
                                          <MenuItem key={dia} value={dia.toString()}>
                                            {dia}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                      {fieldState.error && (
                                        <FormHelperText>{fieldState.error.message}</FormHelperText>
                                      )}
                                    </FormControl>
                                  )}
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <Controller
                                  name={`delivery_details_attributes.${index}.gauge`}
                                  control={control}
                                  render={({ field, fieldState }) => (
                                    <FormControl
                                      fullWidth
                                      size="small"
                                      error={!!fieldState.error}
                                      disabled={!selectedProgramId}
                                    >
                                      <Typography variant="caption" color="text.secondary" gutterBottom>
                                        {t('programs.gauge')} *
                                      </Typography>
                                      <Select
                                        {...field}
                                        value={field.value || ""}
                                        displayEmpty
                                        MenuProps={{
                                          PaperProps: {
                                            style: {
                                              maxHeight: 300,
                                            },
                                          },
                                          disableScrollLock: true,
                                        }}
                                      >
                                        <MenuItem value="" disabled>
                                          {!selectedProgramId ? "Select Program" : "Select"}
                                        </MenuItem>
                                        {availableGauges.map((gauge) => (
                                          <MenuItem key={gauge} value={gauge}>
                                            {gauge} GG
                                          </MenuItem>
                                        ))}
                                      </Select>
                                      {fieldState.error && (
                                        <FormHelperText>{fieldState.error.message}</FormHelperText>
                                      )}
                                    </FormControl>
                                  )}
                                />
                              </Grid>
                            </Grid>

                            <Controller
                              name={`delivery_details_attributes.${index}.machine_type`}
                              control={control}
                              render={({ field, fieldState }) => (
                                <FormControl
                                  fullWidth
                                  size="small"
                                  error={!!fieldState.error}
                                  disabled={!selectedProgramId}
                                >
                                  <Typography variant="caption" color="text.secondary" gutterBottom>
                                    {t('deliveries.machineType')} *
                                  </Typography>
                                  <Select
                                    {...field}
                                    value={field.value || ""}
                                    displayEmpty
                                    MenuProps={{
                                      PaperProps: {
                                        style: {
                                          maxHeight: 300,
                                        },
                                      },
                                      disableScrollLock: true,
                                    }}
                                  >
                                    <MenuItem value="" disabled>
                                      {!selectedProgramId ? "Select Program" : "Select Type"}
                                    </MenuItem>
                                    {availableMachineTypes.map((type) => (
                                      <MenuItem key={type} value={type}>
                                        {type.charAt(0).toUpperCase() + type.slice(1)}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  {fieldState.error && (
                                    <FormHelperText>{fieldState.error.message}</FormHelperText>
                                  )}
                                </FormControl>
                              )}
                            />

                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <Controller
                                  name={`delivery_details_attributes.${index}.rolls`}
                                  control={control}
                                  render={({
                                    field: { onChange, value, ...fieldProps },
                                    fieldState,
                                  }) => (
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                                        {t('deliveries.rolls')} *
                                      </Typography>
                                      <TextField
                                        {...fieldProps}
                                        value={value || ""}
                                        onChange={(e) =>
                                          onChange(
                                            e.target.value
                                              ? Number(e.target.value)
                                              : undefined
                                          )
                                        }
                                        type="number"
                                        size="small"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        fullWidth
                                      />
                                    </Box>
                                  )}
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <Controller
                                  name={`delivery_details_attributes.${index}.weight`}
                                  control={control}
                                  render={({
                                    field: { onChange, value, ...fieldProps },
                                    fieldState,
                                  }) => (
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                                        {t('deliveries.weightKg')} *
                                      </Typography>
                                      <TextField
                                        {...fieldProps}
                                        value={value || ""}
                                        onChange={(e) =>
                                          onChange(
                                            e.target.value
                                              ? Number(e.target.value)
                                              : undefined
                                          )
                                        }
                                        type="number"
                                        size="small"
                                        inputProps={{ step: "0.01" }}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        fullWidth
                                      />
                                    </Box>
                                  )}
                                />
                              </Grid>
                            </Grid>
                          </Stack>
                        </Paper>
                      );
                    })
                  )}
                </Stack>
              ) : (
                /* Desktop Table View */
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ minWidth: 100 }}>{t('programs.dia')}</TableCell>
                        <TableCell sx={{ minWidth: 100 }}>{t('programs.gaugeGG')}</TableCell>
                        <TableCell sx={{ minWidth: 120 }}>{t('deliveries.machineType')}</TableCell>
                        <TableCell sx={{ minWidth: 100 }}>{t('deliveries.rolls')}</TableCell>
                        <TableCell sx={{ minWidth: 120 }}>{t('deliveries.weightKg')}</TableCell>
                        <TableCell width={60} align="center">{t('action')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fields.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography variant="body2" color="text.secondary">
                              {t('noDetailsAdded')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        fields.map((field, index) => {
                          const selectedDia = watch(`delivery_details_attributes.${index}.dia`);
                          const selectedGauge = watch(`delivery_details_attributes.${index}.gauge`);
                          const selectedMachineType = watch(`delivery_details_attributes.${index}.machine_type`);
                          
                          const availableDias = getAvailableDias(selectedProgramId, selectedGauge, selectedMachineType);
                          const availableGauges = getAvailableGauges(selectedProgramId, selectedDia, selectedMachineType);
                          const availableMachineTypes = getAvailableMachineTypes(selectedProgramId, selectedDia, selectedGauge);

                          return (
                            <TableRow key={field.id}>
                              <TableCell>
                                <Controller
                                  name={`delivery_details_attributes.${index}.dia`}
                                  control={control}
                                  render={({ field, fieldState }) => (
                                    <FormControl
                                      fullWidth
                                      size="small"
                                      error={!!fieldState.error}
                                      disabled={!selectedProgramId}
                                    >
                                      <Select
                                        {...field}
                                        value={field.value || ""}
                                        displayEmpty
                                      >
                                        <MenuItem value="" disabled>
                                          {!selectedProgramId ? "Select Program First" : "Select Dia"}
                                        </MenuItem>
                                        {availableDias.map((dia) => (
                                          <MenuItem key={dia} value={dia.toString()}>
                                            {dia}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                      {fieldState.error && (
                                        <FormHelperText>{fieldState.error.message}</FormHelperText>
                                      )}
                                    </FormControl>
                                  )}
                                />
                              </TableCell>
                              <TableCell>
                                <Controller
                                  name={`delivery_details_attributes.${index}.gauge`}
                                  control={control}
                                  render={({ field, fieldState }) => (
                                    <FormControl
                                      fullWidth
                                      size="small"
                                      error={!!fieldState.error}
                                      disabled={!selectedProgramId}
                                    >
                                      <Select
                                        {...field}
                                        value={field.value || ""}
                                        displayEmpty
                                      >
                                        <MenuItem value="" disabled>
                                          {!selectedProgramId ? "Select Program First" : "Select Gauge"}
                                        </MenuItem>
                                        {availableGauges.map((gauge) => (
                                          <MenuItem key={gauge} value={gauge}>
                                            {gauge} GG
                                          </MenuItem>
                                        ))}
                                      </Select>
                                      {fieldState.error && (
                                        <FormHelperText>{fieldState.error.message}</FormHelperText>
                                      )}
                                    </FormControl>
                                  )}
                                />
                              </TableCell>
                              <TableCell>
                                <Controller
                                  name={`delivery_details_attributes.${index}.machine_type`}
                                  control={control}
                                  render={({ field, fieldState }) => (
                                    <FormControl
                                      fullWidth
                                      size="small"
                                      error={!!fieldState.error}
                                      disabled={!selectedProgramId}
                                    >
                                      <Select
                                        {...field}
                                        value={field.value || ""}
                                        displayEmpty
                                      >
                                        <MenuItem value="" disabled>
                                          {!selectedProgramId ? "Select Program First" : "Select Type"}
                                        </MenuItem>
                                        {availableMachineTypes.map((type) => (
                                          <MenuItem key={type} value={type}>
                                            {type.charAt(0).toUpperCase() + type.slice(1)}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                      {fieldState.error && (
                                        <FormHelperText>{fieldState.error.message}</FormHelperText>
                                      )}
                                    </FormControl>
                                  )}
                                />
                              </TableCell>
                              <TableCell>
                                <Controller
                                  name={`delivery_details_attributes.${index}.rolls`}
                                  control={control}
                                  render={({
                                    field: { onChange, value, ...fieldProps },
                                    fieldState,
                                  }) => (
                                    <TextField
                                      {...fieldProps}
                                      value={value || ""}
                                      onChange={(e) =>
                                        onChange(
                                          e.target.value
                                            ? Number(e.target.value)
                                            : undefined
                                        )
                                      }
                                      type="number"
                                      size="small"
                                      error={!!fieldState.error}
                                      helperText={fieldState.error?.message}
                                      fullWidth
                                    />
                                  )}
                                />
                              </TableCell>
                              <TableCell>
                                <Controller
                                  name={`delivery_details_attributes.${index}.weight`}
                                  control={control}
                                  render={({
                                    field: { onChange, value, ...fieldProps },
                                    fieldState,
                                  }) => (
                                    <TextField
                                      {...fieldProps}
                                      value={value || ""}
                                      onChange={(e) =>
                                        onChange(
                                          e.target.value
                                            ? Number(e.target.value)
                                            : undefined
                                        )
                                      }
                                      type="number"
                                      size="small"
                                      inputProps={{ step: "0.01" }}
                                      error={!!fieldState.error}
                                      helperText={fieldState.error?.message}
                                      fullWidth
                                    />
                                  )}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  color="error"
                                  onClick={() => remove(index)}
                                  disabled={fields.length === 1}
                                  size="small"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
                <Stack 
                  direction={{ xs: 'column', sm: 'row' }} 
                  spacing={{ xs: 1, sm: 3 }}
                >
                  <Typography variant="body2">
                    <strong>{t('calculatedTotalRolls')}:</strong> {calculateTotalRolls()}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t('calculatedTotalWeight')}:</strong> {calculateTotalWeight()} kg
                  </Typography>
                </Stack>
              </Box>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            sx={{ mt: 3 }}
          >
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size={isMobile ? "large" : "medium"}
            >
              {t('deliveries.createTitle')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate("/deliveries")}
              fullWidth
              size={isMobile ? "large" : "medium"}
            >
              {t('cancel')}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Container>
  );
}

